// Observe rendered customer-app controls without reading React internals or hidden state.
const appUiObservationApi = (() => {
  const excluded = '.superplumma-chat, .invoice-control, .input-password, [data-assistant-exclude], .support-fab, .support-panel, script, style, template, [hidden], [aria-hidden="true"], [inert]'
  const selector = 'button, input, textarea, select, label, a[href], summary, [role="button"], [role="tab"], [role="checkbox"], [role="switch"], [role="option"], [data-app-control]'
  const compact = (value: unknown, limit = 200) => String(value || '').replace(/\s+/g, ' ').trim().slice(0, limit)
  const visible = (element: Element) => {
    if (!element.isConnected || element.closest(excluded) || !element.getClientRects().length) return false
    for (let node: Element | null = element; node; node = node.parentElement) {
      const style = node.ownerDocument.defaultView!.getComputedStyle(node)
      if (style.display === 'none' || style.visibility === 'hidden') return false
      // A background tab can hold a rendered fade-in dialog/toast at its first
      // keyframe. Static transparent fields still remain excluded.
      if (style.opacity === '0' && !node.getAnimations?.().some((animation) => animation.playState === 'running')) return false
    }
    return true
  }
  const readableText = (root: Element, limit: number, skipFields = false) => {
    const walker = root.ownerDocument.createTreeWalker(root, 4)
    let result = '', node: Node | null
    while ((node = walker.nextNode()) && result.length < limit) {
      if (node.parentElement && visible(node.parentElement) && !(skipFields && node.parentElement.closest('select, textarea, input'))) result += ` ${node.textContent || ''}`
    }
    return compact(result, limit)
  }
  const label = (element: any) => compact(element.getAttribute('aria-label') ||
    String(element.getAttribute('aria-labelledby') || '').split(/\s+/).map((id) => element.ownerDocument.getElementById(id)?.textContent || '').join(' ') ||
    Array.from(element.labels || []).map((item: any) => readableText(item, 200, true)).join(' ') ||
    element.closest('.field')?.querySelector('label')?.textContent ||
    element.getAttribute('title') || element.getAttribute('placeholder') ||
    (['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName) ? element.name : readableText(element, 200)))
  const describe = (element: any, query = '') => {
    const proxy = element.tagName === 'LABEL' ? element.querySelector('input[type="checkbox"], input[type="radio"]') : null
    const type = String(proxy?.type || element.getAttribute('role') || element.type || element.tagName).toLowerCase()
    const sensitive = ['password', 'file', 'hidden'].includes(type) || /password|one-time-code|cc-/.test(element.autocomplete || '')
    const nativeField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)
    const context = element.closest('tr, [role="row"], .field, .form-group, fieldset, .card') || element.parentElement
    return {
      label: label(element), type, approvalRequired: Boolean(element.closest('[data-assistant-approval="required"]')),
      icon: element.querySelector('[data-app-control-icon]')?.getAttribute('data-app-control-icon') || '',
      context: context ? readableText(context, 240) : '',
      disabled: Boolean(proxy?.disabled || element.disabled || element.matches(':disabled') || element.closest('fieldset[disabled], [aria-disabled="true"]')),
      readOnly: Boolean(element.readOnly), requiresUser: sensitive,
      ...(nativeField && !sensitive ? { value: compact(element.value, 1000) } : {}),
      ...(['checkbox', 'radio', 'switch'].includes(type) ? { checked: proxy ? proxy.checked : nativeField ? element.checked : element.getAttribute('aria-checked') === 'true' } : {}),
      ...(element.tagName === 'SELECT' ? { optionsTotal: element.options.length, options: Array.from(element.options)
        .filter((option: any) => !query || label(element).toLowerCase().includes(query.toLowerCase()) || `${option.value} ${option.textContent}`.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 60).map((option: any) => ({ value: option.value, label: compact(option.textContent), disabled: option.disabled || option.parentElement?.disabled === true })) } : {}),
      ...(element.tagName === 'A' ? { external: new URL(element.href, element.ownerDocument.baseURI).origin !== new URL(element.ownerDocument.baseURI).origin } : {}),
    }
  }
  const collect = (doc: Document, query = '', offset = 0) => {
    const dialogs = Array.from(doc.querySelectorAll('.overlay .modal, .drawer, .avst-drawer, [role="dialog"], [role="alertdialog"]')).filter(visible)
    const menus = Array.from(doc.querySelectorAll('.select-menu-portal, .ss-pop, [role="listbox"]')).filter(visible)
    const menu = menus.at(-1)
    const root = menu?.closest('.ss-pop') || menu || dialogs.at(-1) || doc.querySelector('.app')
    if (!root) return { root: null, text: '', rows: [], total: 0, offset, query, dialog: false }
    const candidates = Array.from(root.querySelectorAll(selector)).filter(visible).filter((element) => {
      if (element.tagName !== 'LABEL') return true
      const input = element.querySelector('input[type="checkbox"], input[type="radio"]')
      return input && !visible(input)
    })
    const all = candidates.map((element) => ({ element, ...describe(element, query) }))
    const matching = all.filter((row) => !query || `${row.label} ${row.context}`.toLowerCase().includes(query.toLowerCase()) || row.options?.some((option: any) => `${option.label} ${option.value}`.toLowerCase().includes(query.toLowerCase())))
    const rows: typeof matching = []
    let size = 0
    for (const row of matching.slice(offset, offset + 40)) {
      const data: any = { ...row }
      delete data.element
      const length = JSON.stringify(data).length
      if (size + length > 12000) break
      rows.push(row); size += length
    }
    const statuses = Array.from(doc.querySelectorAll('.toast, [role="status"], [role="alert"]')).filter(visible).slice(-8).map((element) => readableText(element, 400))
    return { root, text: readableText(root, 5000), statuses, rows, total: matching.length, offset, query, dialog: dialogs.length > 0 }
  }
  return { collect, describe, visible }
})()
Object.assign(globalThis, { PlummeAppUiObservation: appUiObservationApi })
